// URL scheme inspection intentionally includes ASCII/C1 control ranges.
// eslint-disable-next-line no-control-regex
const rawControlCharacterPattern = /[\u0000-\u001f\u007f-\u009f]/;
// eslint-disable-next-line no-control-regex
const schemeWhitespacePattern = /[\s\u0000-\u001f\u007f-\u009f]+/g;
const allowedEmailLinkSchemes = new Set(["http", "https", "mailto", "tel"]);
const bareEmailAddressPattern =
  /^[^\s@/:?#]+@[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}(?:\?\S*)?$/i;
const barePhoneNumberPattern = /^\+?[\d\s().-]+$/;
const bareWebAddressPattern =
  /^(?:www\.[^\s/?#]+|[a-z0-9-]+(?:\.[a-z0-9-]+)*\.(?:com|ca|org|net|info|biz|co|io|us|app|news|online|site|shop|store|events|travel))(?::\d+)?(?:[/?#]\S*)?$/i;

/**
 * Staff often type an email address, phone number or website without its
 * scheme. Email clients treat such a value as a relative link, and click
 * tracking then opens the wrong page, so the matching scheme is added.
 */
const addMissingLinkScheme = (value: string): string => {
  if (bareEmailAddressPattern.test(value)) return `mailto:${value}`;
  if (barePhoneNumberPattern.test(value)) {
    const digits = value.replace(/\D/g, "");
    if (digits.length >= 7 && digits.length <= 15) {
      return `tel:${value.startsWith("+") ? "+" : ""}${digits}`;
    }
  }
  if (bareWebAddressPattern.test(value)) return `https://${value}`;
  return value;
};

const decodeCodePoint = (match: string, value: number): string =>
  Number.isSafeInteger(value) && value >= 0 && value <= 0x10ffff
    ? String.fromCodePoint(value)
    : match;

const decodeNumericHtmlReferences = (value: string): string =>
  value
    .replace(/&#(\d+);?/g, (match, decimal: string) =>
      decodeCodePoint(match, Number(decimal))
    )
    .replace(/&#x([0-9a-f]+);?/gi, (match, hexadecimal: string) =>
      decodeCodePoint(match, Number.parseInt(hexadecimal, 16))
    )
    .replace(/&colon;?/gi, ":");

const decodeForSchemeInspection = (value: string): string => {
  let decoded = decodeNumericHtmlReferences(value);

  for (let pass = 0; pass < 3; pass += 1) {
    try {
      const next = decodeURIComponent(decoded);
      if (next === decoded) break;
      decoded = decodeNumericHtmlReferences(next);
    } catch (_error) {
      break;
    }
  }

  return decoded;
};

/**
 * Keeps links supported by email clients while rejecting active-content and
 * obfuscated URL schemes. Relative links remain valid for existing drafts.
 * A bare email address, phone number or website receives its scheme.
 */
export const normalizeEmailLinkUrl = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed || rawControlCharacterPattern.test(trimmed)) return "";

  const inspected = decodeForSchemeInspection(trimmed);
  const firstDelimiterIndex = inspected.search(/[:/?#]/);
  if (firstDelimiterIndex < 0 || inspected[firstDelimiterIndex] !== ":") {
    return addMissingLinkScheme(trimmed);
  }

  const scheme = inspected
    .slice(0, firstDelimiterIndex)
    .replace(schemeWhitespacePattern, "")
    .toLowerCase();

  return allowedEmailLinkSchemes.has(scheme) ? trimmed : "";
};
